/*
 * ==========================================================================
 * CTA COPY EXPERIMENT — GA4 measurement
 * ==========================================================================
 * Assignment already happened synchronously in <head>
 * (snippets/ab-cta-experiment-head.liquid). Nothing here affects rendering.
 *
 * DELIVERY: events go DIRECTLY to GA4 via gtag. They deliberately bypass GTM.
 * That is safe from double-counting because GTM has no tags for these event
 * names — nothing in GTM-WPXPSPHD will ever fire on them.
 *
 * The catch this solves: GTM loads gtag.js at window.load + 1500ms, so
 * `window.gtag` does not exist for the first ~4-6 seconds of a page. Events
 * raised before that are queued in memory, flushed as soon as gtag appears,
 * and persisted to sessionStorage so a click that navigates away before gtag
 * loads is replayed on the next page instead of being lost.
 *
 * EVENT NAMES CARRY THE VARIANT AS A SUFFIX (…_a / …_b) on purpose.
 * Windsor.ai's GA4 connector can read `event_name` today with zero setup, but
 * can only read custom dimensions that are registered AND event-scoped — it
 * exposes no user-scoped dimensions at all. Suffixed names make the experiment
 * readable in Windsor immediately, with no GA4 admin and no waiting for a
 * custom dimension to backfill.
 *
 *   cta_exp_view_a  / cta_exp_view_b    exposure (denominator)
 *   cta_exp_click_a / cta_exp_click_b   CTA click (numerator)
 *   cta_exp_modal_a / cta_exp_modal_b   booking modal opened
 *
 * The same values are ALSO sent as `experiment_id` / `experiment_variant`
 * params, so they work as event-scoped custom dimensions later if wanted.
 *
 * NOTE: does NOT emit `cta_click`. That name is already in use by
 * assets/analytics-events.js (~92/mo) and must not be contaminated.
 *
 * Everything is best-effort and wrapped in try/catch. This script must never
 * be able to break a booking.
 */
(function () {
  'use strict';

  var AB = window.KS_CTA_AB;
  if (!AB || !AB.variant) return; // experiment disabled

  var EXPERIMENT = AB.experiment;
  var VARIANT = AB.variant;           // 'a' | 'b'
  var VARIANT_LABEL = AB.variant_label;
  var MODE = AB.mode || 'test';       // 'measure' (baseline) | 'test'

  window.dataLayer = window.dataLayer || [];

  /* =====================================================================
   * GA4 transport: queue -> flush when gtag exists -> replay across pages
   * =================================================================== */
  /* ONE queue is the single source of truth. sessionStorage is only a mirror
   * of it, cleared atomically with it.
   *
   * The earlier version kept events in BOTH an in-memory list and
   * sessionStorage, and flush() drained both — so every queued event reached
   * GA4 twice, inflating exposures and clicks. The tests missed it because they
   * asserted "an event of this name exists" rather than an exact count. Hence
   * the exact-count assertions now in the harness. */
  var QUEUE = [];
  var STORE_KEY = 'ks_cta_ab_pending';
  var MAX_QUEUE = 25;

  function readStored() {
    try {
      var raw = sessionStorage.getItem(STORE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Object.prototype.toString.call(parsed) === '[object Array]' ? parsed : [];
    } catch (e) { return []; }
  }

  function writeStored(list) {
    try {
      if (!list || !list.length) sessionStorage.removeItem(STORE_KEY);
      else sessionStorage.setItem(STORE_KEY, JSON.stringify(list.slice(-MAX_QUEUE)));
    } catch (e) {}
  }

  function gtagReady() {
    return typeof window.gtag === 'function';
  }

  function emit(name, params) {
    try { window.gtag('event', name, params); return true; } catch (e) { return false; }
  }

  /* Adopt whatever a previous page left behind (a click that navigated away
   * before gtag existed). Flagged replayed:true so it can be excluded if exact
   * event timing ever matters. */
  (function adoptStrays() {
    var strays = readStored();
    for (var k = 0; k < strays.length; k++) {
      if (strays[k] && strays[k][1]) strays[k][1].replayed = true;
      QUEUE.push(strays[k]);
    }
  })();

  function enqueue(name, params) {
    QUEUE.push([name, params]);
    if (QUEUE.length > MAX_QUEUE) QUEUE = QUEUE.slice(-MAX_QUEUE);
    writeStored(QUEUE);   // survive an unload before gtag arrives
  }

  function flush() {
    if (!gtagReady()) return false;
    if (!QUEUE.length) return true;
    var items = QUEUE;
    QUEUE = [];
    writeStored([]);      // clear BEFORE emitting so a re-entrant flush finds nothing
    for (var f = 0; f < items.length; f++) emit(items[f][0], items[f][1]);
    return true;
  }

  /* Raise one experiment event. Name carries the variant as a suffix so
   * Windsor can read it via `event_name` with no GA4 admin; the same values
   * also ride along as params for optional custom dimensions later. */
  function track(baseName, extra) {
    try {
      var name = baseName + '_' + VARIANT;
      var params = {
        experiment_id: EXPERIMENT,
        experiment_variant: VARIANT,
        experiment_variant_label: VARIANT_LABEL,
        experiment_mode: MODE
      };
      if (extra) {
        for (var k in extra) {
          if (Object.prototype.hasOwnProperty.call(extra, k)) params[k] = extra[k];
        }
      }

      // dataLayer copy: costs nothing, useful for debugging and future GTM use.
      var dl = { event: name };
      for (var j in params) {
        if (Object.prototype.hasOwnProperty.call(params, j)) dl[j] = params[j];
      }
      window.dataLayer.push(dl);

      enqueue(name, params);
      flush();   // no-op while gtag is absent; sends immediately once it exists
    } catch (e) {}
  }

  /* Poll for gtag. GTM injects it ~4-6s in; give it a generous window then stop. */
  (function waitForGtag() {
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (flush() || tries > 60) clearInterval(timer); // 60 x 500ms = 30s
    }, 500);
    window.addEventListener('load', function () { setTimeout(flush, 2000); });
  })();

  /* Last chance: drain on the way out. */
  window.addEventListener('pagehide', function () { flush(); });

  /* =====================================================================
   * Exposure — the denominator for click-through rate.
   * Only fired on pages that actually contain a CTA in the test, so the
   * rate is clicks-per-exposure rather than clicks-per-session. That is
   * both the correct A/B denominator and a much less noisy one.
   * =================================================================== */
  var exposureSent = false;
  function sendExposure(count) {
    if (exposureSent || !count) return;
    exposureSent = true;
    track('cta_exp_view', {
      cta_count: count,
      page_path: window.location.pathname,
      page_type: window.__kraft_page_category || ''
    });
  }

  /* =====================================================================
   * Label handling
   * =================================================================== */
  function markCtas(root) {
    try {
      var spans = (root || document).querySelectorAll('[data-ks-cta]');
      for (var i = 0; i < spans.length; i++) {
        var el = spans[i].closest('a, button, [data-modal-trigger]');
        if (el && !el.hasAttribute('data-ks-cta-tracked')) {
          el.setAttribute('data-ks-cta-tracked', '1');
        }
      }
      return spans.length;
    } catch (e) { return 0; }
  }

  /* Safety net: remove the losing label from the DOM. Runs before
   * DOMContentLoaded so nothing (e.g. the sticky bar in content-tabs-meta,
   * which copies a hero button's textContent) can ever read both labels
   * concatenated. In 'measure' mode there is only one label, so this is a
   * no-op. */
  function pruneLosers(root) {
    try {
      var loser = VARIANT === 'b' ? 'a' : 'b';
      var dead = (root || document).querySelectorAll('[data-ks-cta="' + loser + '"]');
      for (var i = 0; i < dead.length; i++) {
        if (dead[i].parentNode) dead[i].parentNode.removeChild(dead[i]);
      }
    } catch (e) {}
  }

  function sweep(root) {
    markCtas(root);   // must run BEFORE pruning, while both spans exist
    pruneLosers(root);
  }

  sweep(document);    // immediately on execution, not on DOMContentLoaded

  /* =====================================================================
   * Click tracking — the numerator.
   * =================================================================== */
  var lastClick = 0;
  document.addEventListener('click', function (evt) {
    try {
      var target = evt.target;
      if (!target || !target.closest) return;
      var cta = target.closest('[data-ks-cta-tracked]');
      if (!cta) {
        var late = target.closest('a, button, [data-modal-trigger]');
        if (!late || !late.querySelector('[data-ks-cta]')) return;
        cta = late;
        cta.setAttribute('data-ks-cta-tracked', '1');
      }

      var now = Date.now();
      if (now - lastClick < 250) return;   // guard nested handlers
      lastClick = now;

      track('cta_exp_click', {
        cta_text: (cta.textContent || '').trim().substring(0, 100),
        cta_modal: cta.getAttribute('data-modal-trigger') || '',
        cta_url: cta.getAttribute('href') || '',
        page_path: window.location.pathname,
        page_type: window.__kraft_page_category || ''
      });

      tagCart();
    } catch (e) {}
  }, true);

  /* =====================================================================
   * Booking modal opens — mid-funnel checkpoint.
   * =================================================================== */
  try {
    var seenModals = {};
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var node = records[i].target;
        if (!node || node.nodeType !== 1) continue;
        var id = node.id || '';
        if (id.indexOf('kraft-') !== 0 && id.indexOf('-modal') === -1) continue;
        var open =
          node.classList.contains('is-open') ||
          node.classList.contains('active') ||
          node.getAttribute('aria-hidden') === 'false';
        if (open && !seenModals[id]) {
          seenModals[id] = true;
          track('cta_exp_modal', { modal_id: id, page_path: window.location.pathname });
        } else if (!open) {
          seenModals[id] = false;
        }
      }
    }).observe(document.documentElement, {
      subtree: true, attributes: true, attributeFilter: ['class', 'aria-hidden']
    });
  } catch (e) {}

  /* =====================================================================
   * Shopify cart attribute — order-level attribution that owes nothing to
   * GA4, ad blockers, or cross-domain tracking. Lands on the order, so you
   * can segment bookings by variant straight from an order export.
   *
   * (The GA4 user-property approach was dropped: Windsor exposes no
   * user-scoped custom dimensions, so it would have been unreadable there.)
   *
   * Never called for visitors with no cart — creating a cart for every
   * visitor would pollute Klaviyo abandoned-cart flows.
   * =================================================================== */
  function hasCart() {
    try { return /(?:^|;\s*)cart=/.test(document.cookie); } catch (e) { return false; }
  }

  function tagCart() {
    try {
      if (sessionStorage.getItem('ks_cta_ab_cart_tagged') === VARIANT) return;
      if (!hasCart()) return;
      var attrs = {};
      attrs['CTA Variant'] = VARIANT.toUpperCase();
      attrs['CTA Experiment'] = EXPERIMENT;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).then(function () {
        try { sessionStorage.setItem('ks_cta_ab_cart_tagged', VARIANT); } catch (e) {}
      }).catch(function () {});
    } catch (e) {}
  }

  /* =====================================================================
   * Boot
   * =================================================================== */
  function boot() {
    var count = markCtas(document);
    pruneLosers(document);
    sendExposure(count);
    if (hasCart()) tagCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { sweep(e.target); });
  window.addEventListener('load', function () {
    var n = markCtas(document);
    pruneLosers(document);
    sendExposure(n);
  });
})();
